const TermsConditions = () => (
  <div id="terms" className="mb-16">
    <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 pb-4 border-b-2 border-violet-500">
      Terms &amp; Conditions
    </h2>

    <div className="space-y-8">
      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-shield-check-line text-violet-600 mr-3 mt-1"></i>
          1. Acceptance of Terms
        </h3>
        <p className="text-base text-gray-700 leading-relaxed">
          By accessing and using AZZ Matrix&apos;s IT services, you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services. We reserve the right to modify these terms at any time, and continued use of our services constitutes acceptance of any changes.
        </p>
      </div>

      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-service-line text-violet-600 mr-3 mt-1"></i>
          2. Service Usage &amp; Responsibilities
        </h3>
        <p className="text-base text-gray-700 leading-relaxed mb-4">
          AZZ Matrix provides comprehensive IT support, cloud solutions, cybersecurity services, and related technology services. By using our services, you agree to:
        </p>
        <ul className="space-y-3 ml-8">
          <li className="flex items-start text-gray-700">
            <i className="ri-checkbox-circle-fill text-violet-600 mr-3 mt-1 flex-shrink-0"></i>
            <span>Provide accurate and complete information when requesting services</span>
          </li>
          <li className="flex items-start text-gray-700">
            <i className="ri-checkbox-circle-fill text-violet-600 mr-3 mt-1 flex-shrink-0"></i>
            <span>Use our services only for lawful purposes and in compliance with all applicable laws</span>
          </li>
          <li className="flex items-start text-gray-700">
            <i className="ri-checkbox-circle-fill text-violet-600 mr-3 mt-1 flex-shrink-0"></i>
            <span>Maintain the confidentiality of any login credentials or access information provided</span>
          </li>
          <li className="flex items-start text-gray-700">
            <i className="ri-checkbox-circle-fill text-violet-600 mr-3 mt-1 flex-shrink-0"></i>
            <span>Cooperate with our technicians and provide necessary access to systems for service delivery</span>
          </li>
          <li className="flex items-start text-gray-700">
            <i className="ri-checkbox-circle-fill text-violet-600 mr-3 mt-1 flex-shrink-0"></i>
            <span>Not attempt to reverse engineer, decompile, or interfere with our service infrastructure</span>
          </li>
        </ul>
      </div>

      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-money-dollar-circle-line text-violet-600 mr-3 mt-1"></i>
          3. Payment Terms
        </h3>
        <p className="text-base text-gray-700 leading-relaxed mb-4">
          Payment for AZZ Matrix services is subject to the following terms:
        </p>
        <ul className="space-y-3 ml-8">
          <li className="flex items-start text-gray-700">
            <i className="ri-arrow-right-s-fill text-violet-600 mr-3 mt-1 flex-shrink-0"></i>
            <span><strong>Service Plans:</strong> Subscription-based plans are billed according to the selected billing cycle (monthly, quarterly, or annually).</span>
          </li>
          <li className="flex items-start text-gray-700">
            <i className="ri-arrow-right-s-fill text-violet-600 mr-3 mt-1 flex-shrink-0"></i>
            <span><strong>One-Time Services:</strong> Payment is due upon service completion or as specified in the service agreement.</span>
          </li>
          <li className="flex items-start text-gray-700">
            <i className="ri-arrow-right-s-fill text-violet-600 mr-3 mt-1 flex-shrink-0"></i>
            <span><strong>Late Payments:</strong> Accounts with overdue payments may be subject to service suspension and late fees of 1.5% per month.</span>
          </li>
          <li className="flex items-start text-gray-700">
            <i className="ri-arrow-right-s-fill text-violet-600 mr-3 mt-1 flex-shrink-0"></i>
            <span><strong>Price Changes:</strong> We reserve the right to modify service pricing with 30 days&apos; advance notice to existing customers.</span>
          </li>
        </ul>
      </div>

      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-copyright-line text-violet-600 mr-3 mt-1"></i>
          4. Intellectual Property Rights
        </h3>
        <p className="text-base text-gray-700 leading-relaxed">
          All content, software, documentation, and materials provided by AZZ Matrix remain the intellectual property of AZZ Matrix or its licensors. Clients are granted a limited, non-exclusive, non-transferable license to use our services and related materials solely for their intended business purposes. Unauthorized reproduction, distribution, or modification of our proprietary materials is strictly prohibited.
        </p>
      </div>

      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-lock-password-line text-violet-600 mr-3 mt-1"></i>
          5. Data Privacy &amp; Security
        </h3>
        <p className="text-base text-gray-700 leading-relaxed">
          AZZ Matrix is committed to protecting your data and privacy. We implement industry-standard security measures to safeguard client information. By using our services, you acknowledge that we may collect, process, and store data necessary for service delivery. We will not share your confidential information with third parties except as required by law or with your explicit consent. For detailed information, please refer to our <a href="/legal#privacy" className="text-violet-600 hover:text-violet-700 font-semibold">Privacy Policy</a>.
        </p>
      </div>

      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-alert-line text-violet-600 mr-3 mt-1"></i>
          6. Limitation of Liability
        </h3>
        <p className="text-base text-gray-700 leading-relaxed mb-4">
          While AZZ Matrix strives to provide reliable and high-quality IT services, we cannot guarantee uninterrupted or error-free service. To the maximum extent permitted by law:
        </p>
        <ul className="space-y-3 ml-8">
          <li className="flex items-start text-gray-700">
            <i className="ri-arrow-right-s-fill text-violet-600 mr-3 mt-1 flex-shrink-0"></i>
            <span>AZZ Matrix shall not be liable for any indirect, incidental, special, or consequential damages arising from service use or inability to use services.</span>
          </li>
          <li className="flex items-start text-gray-700">
            <i className="ri-arrow-right-s-fill text-violet-600 mr-3 mt-1 flex-shrink-0"></i>
            <span>Our total liability for any claim shall not exceed the amount paid by the client for services in the 12 months preceding the claim.</span>
          </li>
          <li className="flex items-start text-gray-700">
            <i className="ri-arrow-right-s-fill text-violet-600 mr-3 mt-1 flex-shrink-0"></i>
            <span>We are not responsible for data loss resulting from client actions, third-party services, or force majeure events.</span>
          </li>
          <li className="flex items-start text-gray-700">
            <i className="ri-arrow-right-s-fill text-violet-600 mr-3 mt-1 flex-shrink-0"></i>
            <span>Clients are responsible for maintaining their own data backups and disaster recovery plans.</span>
          </li>
        </ul>
      </div>

      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-time-line text-violet-600 mr-3 mt-1"></i>
          7. Service Level Agreements (SLA)
        </h3>
        <p className="text-base text-gray-700 leading-relaxed">
          Specific service level commitments, including response times, uptime guarantees, and support availability, are detailed in individual service agreements. SLA terms vary by service plan (Basic, Growth, or Pro). Clients should refer to their specific service agreement for detailed SLA provisions and remedies for service level breaches.
        </p>
      </div>

      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-file-damage-line text-violet-600 mr-3 mt-1"></i>
          8. Termination of Services
        </h3>
        <p className="text-base text-gray-700 leading-relaxed mb-4">
          Either party may terminate services under the following conditions:
        </p>
        <ul className="space-y-3 ml-8">
          <li className="flex items-start text-gray-700">
            <i className="ri-arrow-right-s-fill text-violet-600 mr-3 mt-1 flex-shrink-0"></i>
            <span><strong>Client Termination:</strong> Clients may cancel services with 30 days&apos; written notice. Refunds will be processed according to our Refund Policy.</span>
          </li>
          <li className="flex items-start text-gray-700">
            <i className="ri-arrow-right-s-fill text-violet-600 mr-3 mt-1 flex-shrink-0"></i>
            <span><strong>AZZ Matrix Termination:</strong> We reserve the right to terminate services immediately for breach of terms, non-payment, or misuse of services.</span>
          </li>
          <li className="flex items-start text-gray-700">
            <i className="ri-arrow-right-s-fill text-violet-600 mr-3 mt-1 flex-shrink-0"></i>
            <span><strong>Data Retrieval:</strong> Upon termination, clients have 30 days to retrieve their data before it is permanently deleted from our systems.</span>
          </li>
        </ul>
      </div>

      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-scales-3-line text-violet-600 mr-3 mt-1"></i>
          9. Governing Law &amp; Dispute Resolution
        </h3>
        <p className="text-base text-gray-700 leading-relaxed">
          These Terms and Conditions shall be governed by and construed in accordance with the laws of the jurisdiction in which AZZ Matrix operates, without regard to its conflict of law provisions. Any disputes arising from these terms or service use shall first be attempted to be resolved through good-faith negotiation. If negotiation fails, disputes shall be resolved through binding arbitration in accordance with applicable arbitration rules, with the arbitration taking place in our primary business location.
        </p>
      </div>

      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-phone-line text-violet-600 mr-3 mt-1"></i>
          10. Contact Information
        </h3>
        <p className="text-base text-gray-700 leading-relaxed mb-4">
          For questions regarding these Terms and Conditions or our Refund Policy, please contact us:
        </p>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <p className="text-gray-700 mb-2">
            <strong className="text-slate-900">AZZ Matrix</strong>
          </p>
          <p className="text-gray-700 mb-2">
            <i className="ri-mail-line text-violet-600 mr-2"></i>
            Email: <a href="mailto:support@azzmatrix.com" className="text-violet-600 hover:text-violet-700 font-semibold">support@azzmatrix.com</a>
          </p>
          <p className="text-gray-700 mb-2">
            <i className="ri-phone-line text-violet-600 mr-2"></i>
            Phone: <a href="tel:+18729998632" className="text-violet-600 hover:text-violet-700 font-semibold">+1 (872) 999-8632</a>
          </p>
          <p className="text-gray-700">
            <i className="ri-time-line text-violet-600 mr-2"></i>
            Business Hours: Monday – Friday, 9:00 AM – 6:00 PM
          </p>
        </div>
      </div>

      <div className="bg-violet-50 border-l-4 border-violet-600 p-6 rounded-lg mt-8">
        <p className="text-sm text-gray-700 leading-relaxed">
          <strong className="text-violet-900">Important:</strong> By purchasing or using AZZ Matrix services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions and our Refund Policy. These terms constitute a legally binding agreement between you and AZZ Matrix.
        </p>
      </div>
    </div>
  </div>
);

export default TermsConditions;
